/*
 * The `?asOf=` query parameter's grammar, in one place, with no router import so it is
 * unit-testable on its own. `normaliseAsOfQuery` is the one function `router/index.ts`'s
 * `beforeEach` calls; it is what carries the parameter across every `RouterLink` and
 * `router.push` call in the app without touching any of them (rule 5 below).
 */

// library imports
import type { LocationQuery, RouteLocationNormalized, RouteLocationRaw } from 'vue-router';

// local imports
import { isHistoricalDate } from '@backend/services/performance/quarters';

/** The query string key the as-of date lives under. */
export const AS_OF_QUERY = 'asOf';

/**
 * True when `value` is a real calendar-date string strictly before today — the only
 * shape the parameter may legally carry.
 * @param value - the raw query value to validate
 * @returns whether `value` is a valid, historical as-of date
 */
export function isValidAsOf(value: unknown): value is string {
  return typeof value === 'string' && isHistoricalDate(value);
}

/**
 * Reads the validated as-of date off a route's query.
 * @param query - a route's `query` object
 * @returns the validated date, or null when absent or invalid
 */
export function readAsOf(query: LocationQuery): string | null {
  const raw = query[AS_OF_QUERY];

  return isValidAsOf(raw) ? raw : null;
}

/**
 * Router guard for the `?asOf=` parameter. Returns a redirect location when `to` must be
 * rewritten, or `null` to let the navigation proceed unchanged.
 *
 * Rules, checked in order — each one below documents its own reasoning, in particular
 * why rule 5 checks `to.redirectedFrom`.
 * @param to - the navigation target
 * @param from - the navigation origin
 * @returns a redirect location, or null to proceed
 */
export function normaliseAsOfQuery(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
): RouteLocationRaw | null {
  const hasKey = AS_OF_QUERY in to.query;
  const rawValue = to.query[AS_OF_QUERY];

  // 1. Not under a firm route: the date has no meaning outside a firm.
  if (typeof to.params.firmId !== 'string') {
    if (hasKey)
      return withoutAsOf(to);

    return null;
  }

  // 2. Explicit clear — vue-router renders `query: { asOf: null }` as a bare `?asOf`.
  if (hasKey && (rawValue === null || rawValue === ''))
    return withoutAsOf(to, true);

  // 3. Present but invalid (malformed, not real, >= today, or a repeated-key array).
  if (hasKey && !isValidAsOf(rawValue))
    return withoutAsOf(to, true);

  // 4. Cross-firm navigation: a date never survives a firm switch.
  const crossFirm = typeof from.params.firmId === 'string' && from.params.firmId !== to.params.firmId;
  if (crossFirm && hasKey)
    return withoutAsOf(to);

  // 5. Propagate `from`'s value on a same-firm navigation that did not itself mention
  //    the key — including one where it was just rejected by rule 2 or 3.
  const sameFirm = from.params.firmId === to.params.firmId;
  const keyWasRejected = Boolean(to.redirectedFrom && AS_OF_QUERY in to.redirectedFrom.query);

  if (!hasKey && !crossFirm && sameFirm) {
    const fromValue = readAsOf(from.query);
    if (fromValue !== null && !keyWasRejected)
      return { ...to, query: { ...to.query, [AS_OF_QUERY]: fromValue } };
  }

  // 6. Nothing applies.
  return null;
}

/**
 * A redirect to `to` with the as-of key removed.
 * @param to - the navigation target
 * @param replace - whether the redirect should not leave a history entry
 * @returns the stripped location
 */
function withoutAsOf(to: RouteLocationNormalized, replace = false): RouteLocationRaw {
  const query = { ...to.query };
  delete query[AS_OF_QUERY];

  return replace ? { ...to, query, replace: true } : { ...to, query };
}
